import { FloatingPanel } from '@hue/design-system';
import { Check, Moon, Palette, Sun } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import type { SessionUser } from '../lib/api.js';
import { PALETTES, useAppearance } from './appearance.js';

export function ThemeToggle({ user }: { user?: SessionUser | undefined } = {}) {
  const [open, setOpen] = useState(false);
  const { mode, palette, density, choose } = useAppearance(user);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Personalizar aparência"
        aria-expanded={open}
        aria-controls="appearance-panel"
        className="grid size-10 place-items-center rounded-xl border border-surface-border bg-surface-card text-content/65 transition hover:border-brand-500/60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
      >
        <Palette className="size-4" aria-hidden="true" />
      </button>

      {open ? (
        <FloatingPanel
          anchorRef={anchorRef}
          onDismiss={close}
          align="start"
          id="appearance-panel"
          role="dialog"
          aria-label="Aparência"
          className="max-h-[calc(100vh-1rem)] w-[min(21rem,calc(100vw-1rem))] overflow-y-auto rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card"
        >
          <div className="flex items-center justify-between gap-3 border-b border-surface-border px-1 pb-3">
            <div>
              <strong className="block text-sm">Aparência</strong>
              <span className="text-xs text-content-muted">Tema e densidade da interface</span>
            </div>
            <button
              type="button"
              onClick={() => choose({ mode: mode === 'dark' ? 'light' : 'dark' })}
              aria-label={`Usar modo ${mode === 'dark' ? 'claro' : 'escuro'}`}
              className="flex h-9 items-center gap-2 rounded-lg border border-surface-border px-3 text-xs font-semibold text-content-secondary transition hover:border-brand-500/60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
              {mode === 'dark' ? 'Claro' : 'Escuro'}
            </button>
          </div>

          <div className="mt-3" role="radiogroup" aria-label="Tema de cores">
            {PALETTES.map((option) => {
              const selected = option.value === palette;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choose({ palette: option.value })}
                  className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 ${selected ? 'bg-surface-elevated' : 'hover:bg-content/5'}`}
                >
                  <span className="flex overflow-hidden rounded-full border border-surface-border">
                    {option.swatches.map((color) => (
                      <span key={color} className="size-4" style={{ backgroundColor: color }} />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block text-xs">{option.label}</strong>
                    <span className="block truncate text-[11px] text-content-muted">
                      {option.description}
                    </span>
                  </span>
                  {selected ? <Check className="size-4 text-brand-400" aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>

          <div className="mt-3 border-t border-surface-border pt-3">
            <button
              type="button"
              role="switch"
              aria-checked={density === 'compact'}
              onClick={() => choose({ density: density === 'compact' ? 'comfortable' : 'compact' })}
              className="flex w-full items-center justify-between gap-4 rounded-xl px-2 py-2 text-left transition hover:bg-content/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              <span>
                <strong className="block text-xs">Versão compacta</strong>
                <span className="text-[11px] text-content-muted">Menos espaço, mais conteúdo</span>
              </span>
              <span
                aria-hidden="true"
                className={`flex h-5 w-9 items-center rounded-full p-0.5 transition ${density === 'compact' ? 'justify-end bg-brand-500' : 'bg-surface-border'}`}
              >
                <span className="size-4 rounded-full bg-white shadow" />
              </span>
            </button>
          </div>
        </FloatingPanel>
      ) : null}
    </>
  );
}
